import { type SessionSummaryQuery } from "@crowdcoursing/api/graphql";
import { Box, Grid } from "@mui/material";
import { type DateTime } from "luxon";
import { useMemo } from "react";

// User-facing strings
const STRINGS = {
  JOIN_EVENT_PRIVATE: "Request to join this event to see schedules, activities, and more.",
  JOIN_EVENT_PUBLIC: "Join this event to see schedules, activities, and more.",
  JOIN_EVENT_TITLE: "Join Event to View Details",
} as const;

import { BlurOverlay } from "~ui/molecules/BlurOverlay/BlurOverlay";
import { CalendarEventDetails } from "~ui/molecules/CalendarEventDetails/CalendarEventDetails";
import { CollapsibleDescription } from "~ui/molecules/CollapsibleDescription/CollapsibleDescription";
import { type Member } from "~ui/molecules/MemberCard/types";
import { type Milestone, type Task, TasksMilestonesCard } from "~ui/molecules/TasksMilestonesCard/TasksMilestonesCard";
import { TodaySchedule } from "~ui/molecules/TodaySchedule/TodaySchedule";
import { type MeetingTimeData } from "~ui/organisms/AddEventTimesModal/AddEventTimesModal.types";
import { ChatSlideout } from "~ui/organisms/ChatSlideout/ChatSlideout";
import { EventActivityCards } from "~ui/organisms/EventActivityCards/EventActivityCards";
import { EventCalendar } from "~ui/organisms/EventCalendar/EventCalendar";
import { EventHeader } from "~ui/organisms/EventHeader/EventHeader";
import { EventTimesSlideout } from "~ui/organisms/EventTimesSlideout/EventTimesSlideout";
import { MembersSlideout } from "~ui/organisms/MembersSlideout/MembersSlideout";
import { ResourcesSlideout } from "~ui/organisms/ResourcesSlideout/ResourcesSlideout";

// Extract the specific event type from the query result
type EventFromQuery = NonNullable<SessionSummaryQuery["session"]>;

export interface EventDashboardTemplateProps {
  event: EventFromQuery;
  imageUrl?: string;
  status?: "idle" | "loading" | "error" | "success";
  onImageChange?: (file: File) => void;
  onJoinEvent?: () => Promise<void>;
  onLeaveEvent?: () => Promise<void>;
  onCancelRequest?: () => Promise<void>;
  onAddMember?: (member: { firstName: string; lastName: string; email: string }) => void;
  onDeleteEvent?: () => void;
  onShareEvent?: () => void;
  onManageRules?: () => void;
  onManageSeries?: () => void;
  // Member management props
  members?: Member[];
  requests?: Member[];
  onRemoveMember?: (memberId: string) => void;
  onApproveMember?: (memberId: string) => void;
  onRejectMember?: (memberId: string) => void;
  onAddFacilitator?: (memberId: string) => void;
  onRemoveFacilitator?: (memberId: string) => void;
  onAddOwner?: (memberId: string) => void;
  // Resource management props
  onAddResource?: (resource: { name: string; description: string; url: string }) => void;
  onEditResource?: (
    resource: { name: string; description: string; url: string },
    oldResource: { id: string; name: string; description: string; url: string },
  ) => void;
  onDeleteResource?: (resource: { id: string; name: string; description: string; url: string }) => void;
  // RSVP management props
  isUserAttending?: boolean;
  onRSVP: (sessionId: string) => void;
  onCancelRSVP: (sessionId: string) => void;
  recentAttendees?: {
    id: string;
    name: string;
    email: string;
    isVetted: boolean;
  }[];
  rsvpData?: {
    id: string;
    meetingTime: {
      beginMeetingTime: string;
      endMeetingTime: string;
      id: string;
    };
    user: {
      email: string | null;
      firstName: string | null;
      id: string;
      lastName: string | null;
    };
  }[];
  // Selected meeting time props
  selectedMeetingTime?: {
    id: string;
    startTime: string;
    endTime: string;
  };
  onSelectMeetingTime: (meetingTime: { id: string; startTime: string; endTime: string } | undefined) => void;
  // Selected day props
  selectedDate?: DateTime;
  onSelectDate?: (date: DateTime | undefined) => void;
  // Current month props
  currentMonth: DateTime;
  onMonthChange: (month: DateTime) => void;
  // Meeting time management
  onCreateMeetingTimes: (meetingTimes: MeetingTimeData[], eventId: string) => void;
  onDeleteMeetingTime?: (meetingTimeId: string) => void;
  isCreatingMeetingTimes?: boolean;
  // Chat props
  chatMemberCount?: number;
  // Slideout state
  resourcesSlideoutOpen: boolean;
  membersSlideoutOpen: boolean;
  chatSlideoutOpen: boolean;
  onResourcesClose: () => void;
  onMembersClose: () => void;
  onChatClose: () => void;
  onResourcesClick: () => void;
  onMembersClick: () => void;
  onChatClick: () => void;
  // Event times slideout state
  eventTimesSlideoutOpen: boolean;
  onEventTimesClose: () => void;
  onEventTimesClick: () => void;
  onUpdateMeetingTime?: (
    meetingTimeId: string,
    updates: {
      seriesId?: string | null;
      capacity?: number | null;
      beginTime?: string;
      endTime?: string;
    },
  ) => void;
  availableSeries?: { id: string; name: string; description: string }[];
  // Task management props
  onAddTask?: (task: { name: string; description: string; dueDate: Date }) => void;
  onDeleteTask?: (task: Task) => void;
  onCompleteTask?: (task: Task) => void;
  // Milestone management props
  onAddMilestone?: (milestone: { name: string; description: string; dueDate: Date }) => void;
  onDeleteMilestone?: (milestone: Milestone) => void;
  onCompleteMilestone?: (milestone: Milestone) => void;
  onUpdateEvent?: (event: {
    bubbleId: string;
    description: string;
    isAutoJoin: boolean;
    isSearchable: boolean;
    name: string;
  }) => void;
  bubbleSelectItems?: { id: string; value: string }[];
}

export const EventDashboardTemplate = ({
  event,
  imageUrl,
  status,
  onImageChange,
  onJoinEvent,
  onLeaveEvent,
  onCancelRequest,
  onAddMember,
  members = [],
  requests = [],
  onRemoveMember,
  onApproveMember,
  onRejectMember,
  onAddFacilitator,
  onRemoveFacilitator,
  onAddOwner,
  onAddResource,
  onEditResource,
  onDeleteResource,
  chatMemberCount,
  resourcesSlideoutOpen,
  membersSlideoutOpen,
  chatSlideoutOpen,
  onResourcesClose,
  onMembersClose,
  onChatClose,
  onResourcesClick,
  onMembersClick,
  onChatClick,
  onShareEvent,
  onDeleteEvent,
  onManageRules,
  onManageSeries,
  // RSVP props
  isUserAttending,
  onRSVP,
  onCancelRSVP,
  rsvpData,
  // Selected meeting time props
  selectedMeetingTime,
  onSelectMeetingTime,
  // Selected day props
  selectedDate,
  onSelectDate,
  // Current month props
  currentMonth,
  onMonthChange,
  onCreateMeetingTimes,
  onDeleteMeetingTime,
  isCreatingMeetingTimes,
  // Event times slideout state
  eventTimesSlideoutOpen,
  onEventTimesClose,
  onEventTimesClick,
  onUpdateMeetingTime,
  onAddTask,
  onDeleteTask,
  onCompleteTask,
  onAddMilestone,
  onDeleteMilestone,
  onCompleteMilestone,
  onUpdateEvent,
  bubbleSelectItems,
}: EventDashboardTemplateProps) => {
  const resources = event.resources || [];

  // Calculate member count from provided data
  const memberCount = members.length;
  const requestCount = event.userIsFacilitator ? requests.length : undefined;

  // Check if user can manage resources/members (facilitator or owner)
  const canManageResources = event.userIsFacilitator || event.userIsOwner;
  const canManageMembers = event.userIsFacilitator || event.userIsOwner;
  const canSeeMemberStatus = event.userIsFacilitator || event.userIsOwner;

  // Transform event meeting times to include attendee information
  const eventTimesWithAttendees = useMemo(() => {
    return (event.meetingTimes ?? []).map((mt) => {
      const rsvpsForTime = rsvpData?.filter((rsvp) => rsvp.meetingTime.id === mt.id) ?? [];

      // Find the series this meeting time belongs to
      const series = mt.seriesId ? event.series?.find((s) => s.id === mt.seriesId) : undefined;

      return {
        attendees: rsvpsForTime.map((rsvp) => {
          const memberInfo = members?.find((member) => member.id === rsvp.user.id);
          return {
            email: rsvp.user.email,
            firstName: rsvp.user.firstName,
            id: rsvp.user.id,
            isVetted: Boolean(memberInfo?.isVetted),
            lastName: rsvp.user.lastName,
          };
        }),
        beginMeetingTime: mt.beginMeetingTime,
        capacity: mt.capacity,
        endMeetingTime: mt.endMeetingTime,
        id: mt.id,
        series,
        seriesId: mt.seriesId || undefined,
        userHasRsvpd: mt.userHasRsvpd,
      };
    });
  }, [event.meetingTimes, event.series, rsvpData, members]);

  return (
    <Box>
      {/* Event Header */}
      <EventHeader
        event={event}
        members={members.map((member) => ({
          email: member.email || "",
          firstName: member.firstName || "",
          id: member.id,
          isFacilitator: member.isFacilitator,
          isOwner: member.isOwner,
          lastName: member.lastName || "",
          roles: member.roles,
        }))}
        imageUrl={imageUrl}
        status={status}
        onImageChange={onImageChange}
        onShareEvent={onShareEvent}
        onDeleteEvent={onDeleteEvent}
        onJoinEvent={event.userIsMember || event.userIsFacilitator ? undefined : onJoinEvent}
        onLeaveEvent={
          event.userIsOwner ? undefined : event.userIsMember || event.userIsFacilitator ? onLeaveEvent : undefined
        }
        onCancelRequest={event.userIsPending ? onCancelRequest : undefined}
        onUpdateEvent={onUpdateEvent}
        onManageRules={event.userIsFacilitator ? onManageRules : undefined}
        onManageSeries={event.userIsFacilitator ? onManageSeries : undefined}
        bubbleSelectItems={bubbleSelectItems}
      />
      {/* Description */}
      <Box sx={{ mb: 3 }}>
        <CollapsibleDescription description={event.description} />
      </Box>
      {/* Main Content Section with Two Separate Blur Overlays */}
      {/* First section: Activity Cards, Schedule, and Tasks with strict membership requirement */}
      <BlurOverlay
        isMember={event.userIsMember || event.userIsOwner}
        isPublic={event.isPublic}
        showOverlayText
        overlayStrings={{
          joinPrivate: STRINGS.JOIN_EVENT_PRIVATE,
          joinPublic: STRINGS.JOIN_EVENT_PUBLIC,
          joinTitle: STRINGS.JOIN_EVENT_TITLE,
        }}
      >
        {/* Quick Stats */}
        <EventActivityCards
          event={{
            id: event.id,
            meetingTimes: eventTimesWithAttendees,
            name: event.name,
            resources: event.resources.map((r) => ({
              description: r.description ?? "",
              id: r.id,
              name: r.name,
              url: r.url,
            })),
          }}
          memberCount={memberCount}
          requestCount={requestCount}
          chatMemberCount={chatMemberCount || 0}
          onMembersClick={onMembersClick}
          onResourcesClick={onResourcesClick}
          onChatClick={onChatClick}
          onEventTimesClick={onEventTimesClick}
        />

        {/* First Row of Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Today's Schedule */}
          <Grid
            sx={{ display: "flex" }}
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <TodaySchedule meetingTimes={event.meetingTimes ?? []} />
            </Box>
          </Grid>

          {/* Tasks & Milestones */}
          <Grid
            sx={{ display: "flex" }}
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <TasksMilestonesCard
                tasks={event.eventTasks || []}
                milestones={event.eventMilestones || []}
                canManage={event.userIsFacilitator || event.userIsOwner}
                onAddTask={onAddTask}
                onDeleteTask={onDeleteTask}
                onCompleteTask={onCompleteTask}
                onAddMilestone={onAddMilestone}
                onDeleteMilestone={onDeleteMilestone}
                onCompleteMilestone={onCompleteMilestone}
              />
            </Box>
          </Grid>
        </Grid>
      </BlurOverlay>
      {/* Second section: Calendar and RSVP with relaxed visibility */}
      <BlurOverlay
        isMember={event.userIsMember || event.userIsOwner || (event.isPublic && event.isSearchable)}
        isPublic={event.isPublic}
        showOverlayText={false}
      >
        <Grid container spacing={3}>
          {/* Calendar View */}
          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <EventCalendar
              event={event}
              selectedMeetingTimeId={selectedMeetingTime?.id}
              onSelectMeetingTime={(meetingTime) => {
                if (meetingTime) {
                  onSelectMeetingTime({
                    endTime: meetingTime.endTime,
                    id: meetingTime.id,
                    startTime: meetingTime.startTime,
                  });
                } else {
                  onSelectMeetingTime(undefined);
                }
              }}
              selectedDate={selectedDate}
              onSelectDate={onSelectDate}
              currentMonth={currentMonth}
              onMonthChange={onMonthChange}
            />
          </Grid>

          {/* RSVP Management */}
          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <CalendarEventDetails
              selectedDate={selectedDate}
              meetingTimes={eventTimesWithAttendees}
              selectedMeetingTime={selectedMeetingTime}
              onSelectMeetingTime={onSelectMeetingTime}
              event={event}
              onCreateMeetingTimes={onCreateMeetingTimes}
              isCreatingMeetingTimes={isCreatingMeetingTimes}
              onRSVP={async (sessionId) => {
                // If user is not a member, join the room first
                if (!event.userIsMember && !event.userIsFacilitator && !event.userIsOwner) {
                  await onJoinEvent?.();
                }
                onRSVP(sessionId);
              }}
              onCancelRSVP={onCancelRSVP}
              isUserAttending={isUserAttending}
              attendees={
                rsvpData
                  ?.filter((rsvp) => rsvp.meetingTime.id === selectedMeetingTime?.id)
                  .map((rsvp) => {
                    const memberInfo = members?.find((member) => member.id === rsvp.user.id);
                    return {
                      email: rsvp.user.email,
                      firstName: rsvp.user.firstName,
                      id: rsvp.user.id,
                      isVetted: Boolean(memberInfo?.isVetted),
                      lastName: rsvp.user.lastName,
                    };
                  }) ?? []
              }
              onDeleteMeetingTime={onDeleteMeetingTime}
            />
          </Grid>
        </Grid>
      </BlurOverlay>
      {/* Slideouts */}
      <ResourcesSlideout
        open={resourcesSlideoutOpen}
        onClose={onResourcesClose}
        resources={resources}
        canManage={canManageResources}
        onAddResource={onAddResource}
        onEditResource={onEditResource}
        onDeleteResource={onDeleteResource}
      />
      <MembersSlideout
        open={membersSlideoutOpen}
        onClose={onMembersClose}
        members={members}
        requests={requests}
        canManage={canManageMembers}
        canSeeStatus={canSeeMemberStatus}
        isOwner={event.userIsOwner}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        onApproveMember={onApproveMember}
        onRejectMember={onRejectMember}
        onAddFacilitator={onAddFacilitator}
        onRemoveFacilitator={onRemoveFacilitator}
        onAddOwner={onAddOwner}
      />
      <ChatSlideout open={chatSlideoutOpen} onClose={onChatClose} eventId={event.id} memberCount={chatMemberCount} />
      <EventTimesSlideout
        open={eventTimesSlideoutOpen}
        onClose={onEventTimesClose}
        eventTimes={eventTimesWithAttendees}
        onRSVP={async (sessionId) => {
          // If user is not a member, join the room first
          if (!event.userIsMember && !event.userIsFacilitator && !event.userIsOwner) {
            await onJoinEvent?.();
          }
          onRSVP(sessionId);
        }}
        onCancelRSVP={onCancelRSVP}
        onDeleteMeetingTime={onDeleteMeetingTime}
        onUpdateMeetingTime={onUpdateMeetingTime}
        onCreateMeetingTimes={onCreateMeetingTimes}
        isCreatingMeetingTimes={isCreatingMeetingTimes}
        eventId={event.id}
        availableSeries={event.series}
        canManage={event.userIsFacilitator}
      />
    </Box>
  );
};
